import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Droplets } from 'lucide-react';

const LOGO_URL =
  'https://customer-assets.emergentagent.com/job_reshadi-replica/artifacts/2d3adpeg_imageedit_14_6586610433.png';

const NodeMotif = ({ tone = 'dark' }) => {
  // tone === 'dark' renders against a light/green background (lines drawn dark/black-ish)
  // tone === 'light' renders against a dark/black background (lines drawn light/white-ish)
  const stroke = tone === 'dark' ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.18)';
  const dot = tone === 'dark' ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.45)';
  const ringStroke = tone === 'dark' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)';
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 600 800"
      className="absolute inset-0 w-full h-full pointer-events-none"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <pattern id={`grid-${tone}`} width="48" height="48" patternUnits="userSpaceOnUse">
          <path d="M 48 0 L 0 0 0 48" fill="none" stroke={ringStroke} strokeWidth="0.6" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#grid-${tone})`} opacity="0.5" />
      {/* Orbital rings */}
      <circle cx="300" cy="500" r="260" fill="none" stroke={ringStroke} strokeWidth="1" />
      <circle cx="300" cy="500" r="200" fill="none" stroke={ringStroke} strokeWidth="1" />
      <circle cx="300" cy="500" r="140" fill="none" stroke={ringStroke} strokeWidth="1" />
      {/* Connection lines */}
      <line x1="120" y1="180" x2="280" y2="320" stroke={stroke} strokeWidth="0.8" strokeDasharray="4,4" />
      <line x1="280" y1="320" x2="460" y2="220" stroke={stroke} strokeWidth="0.8" strokeDasharray="4,4" />
      <line x1="280" y1="320" x2="380" y2="540" stroke={stroke} strokeWidth="0.8" strokeDasharray="4,4" />
      <line x1="380" y1="540" x2="500" y2="660" stroke={stroke} strokeWidth="0.8" strokeDasharray="4,4" />
      <line x1="120" y1="600" x2="380" y2="540" stroke={stroke} strokeWidth="0.8" strokeDasharray="4,4" />
      {/* Nodes */}
      {[
        [120, 180],
        [280, 320],
        [460, 220],
        [380, 540],
        [500, 660],
        [120, 600],
        [240, 720],
      ].map(([cx, cy], i) => (
        <circle key={i} cx={cx} cy={cy} r={4} fill={dot} />
      ))}
    </svg>
  );
};

const SplitLanding = () => {
  const [hovered, setHovered] = useState(null); // 'left' | 'right' | null

  const leftFlex =
    hovered === 'left' ? 'md:flex-[1.08]' : hovered === 'right' ? 'md:flex-[0.92]' : 'md:flex-1';
  const rightFlex =
    hovered === 'right' ? 'md:flex-[1.08]' : hovered === 'left' ? 'md:flex-[0.92]' : 'md:flex-1';

  const leftDim = hovered === 'right' ? 'md:brightness-[0.78]' : 'md:brightness-100';
  const rightDim = hovered === 'left' ? 'md:brightness-[0.78]' : 'md:brightness-100';

  return (
    <main className="relative min-h-screen w-full overflow-hidden bg-black">
      {/* Top-left welcome with small logo */}
      <div
        className="animate-hero-rise absolute top-4 md:top-5 left-4 md:left-6 z-30 flex items-center gap-3 px-3 md:px-4 py-2 md:py-2.5 rounded-full bg-black/85 backdrop-blur-sm border border-white/10 shadow-lg"
        style={{ animationDelay: '0ms' }}
        data-testid="split-welcome"
      >
        <img
          src={LOGO_URL}
          alt="Reshadi"
          className="h-7 md:h-8 w-auto"
          style={{
            filter:
              'brightness(0) saturate(100%) invert(70%) sepia(38%) saturate(536%) hue-rotate(120deg) brightness(95%) contrast(89%)',
          }}
        />
        <span className="text-sm md:text-base font-medium tracking-wide text-white whitespace-nowrap pr-1">
          Welcome to Reshadi
        </span>
      </div>

      {/* The split */}
      <div className="relative z-10 flex flex-col md:flex-row min-h-screen w-full">
        {/* LEFT — Green division */}
        <Link
          to="/regtech"
          data-testid="split-left-link"
          aria-label="Enter Sovereign B2G & AI Solutions"
          onMouseEnter={() => setHovered('left')}
          onMouseLeave={() => setHovered(null)}
          onFocus={() => setHovered('left')}
          onBlur={() => setHovered(null)}
          className={`group relative flex-1 ${leftFlex} ${leftDim} min-h-[60vh] md:min-h-screen overflow-hidden bg-gradient-to-br from-[#3eb5a0] via-[#55c4af] to-[#6dd4bf] transition-all duration-500 ease-out focus:outline-none focus-visible:ring-4 focus-visible:ring-black/40 focus-visible:ring-inset`}
        >
          <NodeMotif tone="dark" />

          <div
            className="animate-hero-rise relative z-10 h-full w-full flex flex-col justify-center px-6 md:px-12 lg:px-20 py-24 md:py-0 md:pr-6 lg:pr-10"
            style={{ animationDelay: '300ms' }}
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-black leading-[1.1] mb-5 md:mb-7 max-w-xl">
              Sovereign B2G<br />&amp; AI Solutions
            </h2>
            <p className="text-lg md:text-xl lg:text-2xl text-black/85 font-light leading-snug max-w-xl mb-8 md:mb-10">
              <ShieldCheck
                size={20}
                strokeWidth={2}
                className="text-black/80 inline-block mr-2 -mt-0.5 align-middle flex-shrink-0"
                aria-hidden="true"
              />
              <span className="align-middle">Our Flagship Project — The CRI Programme</span>
            </p>

            <span
              data-testid="split-left-enter"
              className="inline-flex items-center justify-center gap-3 self-start h-14 w-[180px] bg-black text-white text-base font-semibold rounded-full shadow-lg transition-all duration-300 group-hover:bg-[#2d2d2d] group-hover:-translate-y-0.5 group-hover:shadow-xl"
            >
              <span>Enter</span>
              <span className="w-6 h-6 rounded-full bg-[#3eb5a0] text-black flex items-center justify-center transition-transform duration-300 group-hover:translate-x-1">
                <ArrowRight size={14} strokeWidth={3} />
              </span>
            </span>
          </div>
        </Link>

        {/* Vertical seam (desktop only) */}
        <div
          aria-hidden="true"
          className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-white/0 via-white/30 to-white/0 z-20 pointer-events-none"
        />

        {/* RIGHT — Black division */}
        <Link
          to="/industrial"
          data-testid="split-right-link"
          aria-label="Enter Industrial B2B Solutions"
          onMouseEnter={() => setHovered('right')}
          onMouseLeave={() => setHovered(null)}
          onFocus={() => setHovered('right')}
          onBlur={() => setHovered(null)}
          className={`group relative flex-1 ${rightFlex} ${rightDim} min-h-[60vh] md:min-h-screen overflow-hidden bg-gradient-to-br from-[#1a1a1a] via-[#252525] to-[#2d2d2d] transition-all duration-500 ease-out focus:outline-none focus-visible:ring-4 focus-visible:ring-[#3eb5a0]/50 focus-visible:ring-inset`}
        >
          <NodeMotif tone="light" />

          <div
            className="animate-hero-rise relative z-10 h-full w-full flex flex-col justify-center px-6 md:px-12 lg:px-20 py-24 md:py-0 md:pl-6 lg:pl-10 md:items-end md:text-right"
            style={{ animationDelay: '420ms' }}
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.1] mb-5 md:mb-7 max-w-xl">
              Industrial B2B<br />Solutions
            </h2>
            <p className="text-lg md:text-xl lg:text-2xl text-white/80 font-light leading-snug max-w-xl mb-8 md:mb-10 md:text-right">
              <Droplets
                size={20}
                strokeWidth={2}
                className="text-[#3eb5a0] inline-block mr-2 -mt-0.5 align-middle flex-shrink-0"
                aria-hidden="true"
              />
              <span className="align-middle">Featuring our Industrial Pump Solution</span>
            </p>

            <span
              data-testid="split-right-enter"
              className="inline-flex items-center justify-center gap-3 md:self-end h-14 w-[180px] bg-white text-black text-base font-semibold rounded-full shadow-lg transition-all duration-300 group-hover:bg-[#3eb5a0] group-hover:-translate-y-0.5 group-hover:shadow-xl"
            >
              <span>Enter</span>
              <span className="w-6 h-6 rounded-full bg-black text-white flex items-center justify-center transition-transform duration-300 group-hover:translate-x-1">
                <ArrowRight size={14} strokeWidth={3} />
              </span>
            </span>
          </div>
        </Link>
      </div>
    </main>
  );
};

export default SplitLanding;
